import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Stack, Typography, Paper, TextField, MenuItem, Button,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Box, InputAdornment, CircularProgress, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Link as MuiLink,
  FormControlLabel, Switch, Autocomplete,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import AddIcon from "@mui/icons-material/AddOutlined";
import LaunchIcon from "@mui/icons-material/LaunchOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCartOutlined";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAddOutlined";
import { ChevronUp, ChevronDown } from "lucide-react";
import { apiList, apiPost, apiGet, apiDelete, ApiError } from "../api/client";
import { cacheGet, cacheSet, cacheKey, cacheInvalidate } from "../api/cache";
import { usePersistedState } from "../api/persistedState";
import ImageGalleryCell from "../components/ImageGalleryCell";
import SaleStatusChip from "../components/SaleStatusChip";
import { useToast } from "../ui/Toaster";

// localStorage key for the "needs fulfillment" filter — persists across reloads
// so users who use the page primarily as a fulfillment queue don't have to
// re-toggle every visit.
const LS_NEEDS_FULFILLMENT = "bidrl_sales_needs_fulfillment";

const PLATFORM_OPTIONS = ["", "ebay", "amazon", "facebook", "depop", "other"];

// Marketplace chip colour map — mirrors OrderChecker's OrdersPage so the
// platform column reads at-a-glance with the same palette across products.
const MARKETPLACE_META = {
  ebay:     { label: "eBay",     color: "info" },
  amazon:   { label: "Amazon",   color: "warning" },
  walmart:  { label: "Walmart",  color: "primary" },
  facebook: { label: "Facebook", color: "secondary" },
  depop:    { label: "Depop",    color: "secondary" },
  local:    { label: "Local",    color: "success" },
  other:    { label: "Other",    color: "default" },
};

function useDebounced(value, ms = 300) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return d;
}

/**
 * Item picker for the Add Sale dialog. Searches inventory by title (or
 * lot_id / brand / asin / upc — whatever ``/api/inventory?q=`` matches)
 * and stores the chosen lot's ``lot_id``. The previous version was a
 * raw lot-id text field; this swap is the difference between "I have
 * to know the lot id by heart" and "I type a few title words and pick
 * the right row".
 *
 * Props:
 *   - value:    the picked lot_id ("" when nothing chosen yet)
 *   - onChange: (lot_id) => void
 *   - onClear:  optional — fires when the user explicitly removes this
 *               row (vs blanking it). Drives the growable-list pattern
 *               the caller uses.
 *   - autoFocus: focus the input on mount (used on the freshly-added row)
 *   - canRemove: show the remove button (false for the only row)
 */
function LotPicker({ value, onChange, onClear, autoFocus, canRemove }) {
  const [input, setInput] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Track the option object backing the current value so the input
  // shows the lot's title (not the raw lot_id) once selected. Without
  // this, selecting a row would immediately re-show the lot_id string
  // in the field and lose the search context.
  const [selected, setSelected] = useState(null);
  const debounced = useDebounced(input, 250);

  // Cancel the in-flight request when the user types something newer.
  useEffect(() => {
    if (!debounced || debounced.length < 2 || selected) {
      // Wait for at least 2 chars before hitting the API — single
      // letters return way too many rows and the dropdown becomes
      // useless. Also skip the fetch entirely after a selection so
      // the popup doesn't reopen with stale results.
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    apiGet(
      `/api/inventory?q=${encodeURIComponent(debounced)}&page_size=20&consolidate=false`,
      { signal: ctrl.signal },
    )
      .then((res) => {
        if (ctrl.signal.aborted) return;
        const items = Array.isArray(res?.items) ? res.items : [];
        // Drop sold lots — backend already excludes them by default
        // (show_sold=false), but belt-and-suspenders for any future
        // schema where they could leak through.
        setOptions(items.filter((r) => !r.sold));
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setOptions([]);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [debounced, selected]);

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Autocomplete
        fullWidth
        freeSolo  // lets advanced users still paste a raw lot_id
        size="small"
        options={options}
        loading={loading}
        value={selected}
        inputValue={input}
        onInputChange={(_, v, reason) => {
          // Don't clobber the displayed title when MUI fires "reset"
          // after a selection — that's the source of the bug where
          // the field flipped back to showing the raw lot_id.
          if (reason === "reset") return;
          setInput(v);
        }}
        onChange={(_, picked) => {
          if (!picked) {
            setSelected(null);
            onChange("");
            return;
          }
          if (typeof picked === "string") {
            // freeSolo path — user typed a raw lot_id and hit enter.
            setSelected(null);
            onChange(picked.trim());
            return;
          }
          setSelected(picked);
          onChange(picked.lot_id);
          // Show the title in the field after selection so the user
          // sees what they picked, not the cryptic lot_id.
          setInput(picked.cleaned_title || picked.title || picked.lot_id);
        }}
        getOptionLabel={(opt) => {
          if (!opt) return "";
          if (typeof opt === "string") return opt;
          return opt.cleaned_title || opt.title || opt.lot_id || "";
        }}
        isOptionEqualToValue={(a, b) =>
          (a?.lot_id || a) === (b?.lot_id || b)
        }
        filterOptions={(x) => x}  // disable client-side filtering; server already did it
        noOptionsText={
          !debounced ? "Type to search…" :
          debounced.length < 2 ? "Keep typing…" :
          loading ? "Searching…" : "No matches"
        }
        renderOption={(props, opt) => (
          <li {...props} key={opt.lot_id}>
            <Stack sx={{ width: "100%", minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {opt.cleaned_title || opt.title || "(untitled)"}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{ fontFamily: "monospace", color: "text.secondary" }}
                >
                  {opt.lot_id}
                </Typography>
                {opt.location && (
                  <Chip
                    size="small"
                    label={opt.location}
                    variant="outlined"
                    sx={{ height: 16, fontSize: 10 }}
                  />
                )}
                {opt.condition && (
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    · {opt.condition}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search title or lot id…"
            autoFocus={autoFocus}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading && <CircularProgress size={14} sx={{ mr: 1 }} />}
                  {/* Lot id confirmation chip — sits inside the input
                      so the user always sees the actual id they
                      committed to, even when the title is showing. */}
                  {value && (
                    <Chip
                      size="small"
                      label={value}
                      variant="outlined"
                      sx={{
                        height: 20, mr: 0.5,
                        fontFamily: "monospace", fontSize: 11,
                      }}
                    />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {canRemove && (
        <IconButton
          size="small"
          aria-label="Remove lot"
          onClick={() => onClear?.()}
          sx={{ color: "text.secondary" }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
}

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const profitColor = (n) => (n > 0 ? "success.main" : n < 0 ? "error.main" : "text.secondary");

// Column definitions kept identical in shape/keys to the previous DataTable
// version — only the rendering primitives changed. ``sortable`` lets the
// header click handler skip non-sortable columns (image, link icon).
const COLUMNS = [
  { key: "image_urls",       header: "Image",     width: 80,  sortable: false },
  { key: "title",            header: "Order",     width: undefined, sortable: true },
  // Qty: number of distinct lots linked to this sale. For multi-qty
  // single-line orders (e.g. buyer ordered 3 of one product) this equals
  // the eBay-side line item quantity. Bold/warning when >1 so the user
  // can scan the table for bulk sales.
  { key: "item_count",       header: "Qty",       width: 60,  sortable: true,  align: "right" },
  { key: "sold_at",          header: "Platform",  width: 110, sortable: true },
  { key: "sold_price",       header: "Sold",      width: 90,  sortable: true,  align: "right" },
  { key: "spend",            header: "Spend",     width: 90,  sortable: true,  align: "right" },
  { key: "shipping_cost",    header: "Ship",      width: 80,  sortable: true,  align: "right" },
  { key: "platform_fee",     header: "Fee",       width: 80,  sortable: true,  align: "right" },
  { key: "profit",           header: "Profit",    width: 100, sortable: true,  align: "right" },
  { key: "status",           header: "Status",    width: 130, sortable: false },
  { key: "tracking_number",  header: "Tracking",  width: 110, sortable: false },
  // Fulfillment indicator column — empty header, only renders a Chip when the
  // sale actually needs to ship. Sits between Tracking and Date so it reads
  // alongside the shipping context that determines the flag.
  { key: "needs_fulfillment", header: "",         width: 90,  sortable: false },
  { key: "sold_date",        header: "Date",      width: 110, sortable: true },
  { key: "order_id",         header: "",          width: 60,  sortable: false },
];

export default function Sales() {
  const { error: toastError, success: toastSuccess, info: toastInfo } = useToast();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePersistedState("bidrl_sales_page_size", 50);
  const [search, setSearch] = useState("");
  const [platform, setPlatform] = usePersistedState("bidrl_sales_platform", "");
  const [sortKey, setSortKey] = usePersistedState("bidrl_sales_sort_key", "sold_date");
  const [sortDir, setSortDir] = usePersistedState("bidrl_sales_sort_dir", "desc");
  // "Needs fulfillment" filter — boots from localStorage so the toggle
  // sticks across reloads. ``"true"`` strings are how the backend reads it,
  // matching the ``_qparam_str("needs_fulfillment").lower() in (...)`` check.
  const [needsFulfillmentOnly, setNeedsFulfillmentOnly] = useState(() => {
    try { return localStorage.getItem(LS_NEEDS_FULFILLMENT) === "true"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(LS_NEEDS_FULFILLMENT, needsFulfillmentOnly ? "true" : "false"); }
    catch { /* localStorage unavailable — non-fatal, just don't persist */ }
  }, [needsFulfillmentOnly]);

  const debouncedSearch = useDebounced(search, 300);

  const [modalOpen, setModalOpen] = useState(false);
  const [lotIds, setLotIds] = useState([""]);
  const [formData, setFormData] = useState({
    sold_price: "",
    sold_at: "ebay",
    order_id: "",
    platform_fee: 0,
    shipping_cost: 0,
    tracking_number: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [refreshingFromEbay, setRefreshingFromEbay] = useState(false);

  // "Add lots" dialog state. Used for the rare case where a sale was
  // matched to a single lot but the listing was actually a multi-item
  // bundle (e.g. "Apple Watch Ultra 3 LOT OF 5"). The Match Orders
  // bulk-link path only fires while the sale is still an orphan; once
  // the first lot is attached the orphan disappears from that page, so
  // post-match additions need this dedicated entry point. State holds
  // the target sale row + the comma-separated input + busy flag.
  const [addLotsTarget, setAddLotsTarget] = useState(null);  // { id, title, order_id, existing_lot_ids }
  const [addLotsInput, setAddLotsInput]   = useState("");
  const [addLotsBusy, setAddLotsBusy]     = useState(false);

  const load = useCallback(
    async (signal) => {
      const params = {
        page,
        page_size: pageSize,
        q: debouncedSearch,
        platform,
        sort: sortKey,
        order: sortDir,
        // qs() drops empty strings, so passing "" here means "don't send
        // the param" — keeps the URL clean when the toggle is off.
        needs_fulfillment: needsFulfillmentOnly ? "true" : "",
      };
      const ck = cacheKey("/api/sales", params);
      const cached = cacheGet(ck);
      if (cached) {
        setRows(cached.data.items || []);
        setTotal(cached.data.total || 0);
        setLoading(false);
      } else {
        setLoading(true);
      }
      try {
        const res = await apiList("/api/sales", params, { signal });
        if (signal?.aborted) return;
        const items = (res.items || []).map((row) => ({
          ...row,
          items: Array.isArray(row.items) ? row.items : [],
          lot_ids_text: (row.items || []).map((i) => i.lot_id).filter(Boolean).join(", "),
        }));
        setRows(items);
        setTotal(res.total || 0);
        cacheSet(ck, { items, total: res.total || 0 });
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (!cached) {
          toastError("Couldn't load sales", e instanceof ApiError ? e.message : String(e));
          setRows([]);
          setTotal(0);
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [page, pageSize, debouncedSearch, platform, sortKey, sortDir, needsFulfillmentOnly, toastError]
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  useEffect(() => { setPage(1); }, [debouncedSearch, platform, sortKey, sortDir, pageSize, needsFulfillmentOnly]);

  // Growable list: as soon as the user picks (or types) a lot in the
  // last row, append a fresh empty row underneath so the next pick
  // doesn't need an explicit "add another" button. Removing a row
  // collapses the list; the last row is never removable.
  const handleLotIdChange = (index, value) => {
    let updated = [...lotIds];
    updated[index] = value;
    const tail = updated[updated.length - 1];
    if (String(tail || "").trim()) updated.push("");
    if (updated.length === 0) updated.push("");
    setLotIds(updated);
  };

  const removeLotAt = (index) => {
    setLotIds((cur) => {
      const updated = cur.filter((_, i) => i !== index);
      return updated.length ? updated : [""];
    });
  };

  const submitSale = async (e) => {
    e?.preventDefault?.();
    const cleaned = lotIds.map((id) => String(id || "").trim()).filter(Boolean);
    if (!cleaned.length) {
      toastError("Need at least one lot id");
      return;
    }
    setSubmitting(true);
    try {
      const data = await apiPost("/api/add_sale", {
        lot_ids: cleaned,
        ...formData,
        platform_fee: parseFloat(formData.platform_fee || 0),
        shipping_cost: parseFloat(formData.shipping_cost || 0),
        sold_price: parseFloat(formData.sold_price || 0),
      });
      if (data?.success) {
        toastSuccess("Sale added", data.message);
        setModalOpen(false);
        setLotIds([""]);
        setFormData({ sold_price: "", sold_at: "ebay", order_id: "", platform_fee: 0, shipping_cost: 0, tracking_number: "" });
        cacheInvalidate("/api/sales");
        load();
      } else {
        toastError("Couldn't add sale", data?.error || data?.message);
      }
    } catch (err) {
      // Lot collision — backend queued the order for matching. Surface a
      // useful toast that links to the Match Orders page.
      if (err instanceof ApiError && err.status === 409 && err.body?.queued_for_match) {
        toastInfo(
          "Queued for matching",
          err.body.message || "Open the Match Orders page to pick a different lot."
        );
        setModalOpen(false);
      } else {
        toastError("Couldn't add sale", err instanceof ApiError ? err.message : String(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Submit handler for the "Add lots" dialog. Splits the user's input
  // on any whitespace or comma so they can paste a column from a
  // spreadsheet or type lot ids inline.
  const submitAddLots = async () => {
    if (!addLotsTarget?.id) return;
    const ids = (addLotsInput || "")
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!ids.length) {
      toastError("Enter at least one lot id");
      return;
    }
    setAddLotsBusy(true);
    try {
      const res = await apiPost(`/api/sales/${addLotsTarget.id}/lots`, { lot_ids: ids });
      if (res?.success) {
        toastSuccess(
          `Linked ${res.added} lot${res.added === 1 ? "" : "s"}`,
          `Sale #${addLotsTarget.id} → ${(res.lot_ids || []).join(", ")}`,
        );
        setAddLotsTarget(null);
        setAddLotsInput("");
        cacheInvalidate("/api/sales");
        load();
      } else {
        toastError("Couldn't link", res?.error || "Unknown error");
      }
    } catch (e) {
      toastError("Couldn't link", e instanceof ApiError ? e.message : String(e));
    } finally {
      setAddLotsBusy(false);
    }
  };

  const refreshFromEbay = async () => {
    setRefreshingFromEbay(true);
    toastInfo("Pulling sales from eBay…");
    try {
      await apiGet("/api/update_sales");
      toastSuccess("Synced.");
      cacheInvalidate("/api/sales");
      load();
    } catch (err) {
      toastError("Sync failed", err instanceof ApiError ? err.message : String(err));
    } finally {
      setRefreshingFromEbay(false);
    }
  };

  const onHeaderClick = useCallback((col) => {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col.key);
      setSortDir("asc");
    }
  }, [sortKey, setSortDir, setSortKey]);

  // Sort dropdown options derived from columns — gives users a single
  // affordance equivalent to clicking individual headers, matching the
  // Inventory page's UX (header clicks and dropdown both work).
  const sortOptions = useMemo(() => ([
    { key: "sold_date",   dir: "desc", label: "Newest" },
    { key: "sold_date",   dir: "asc",  label: "Oldest" },
    { key: "profit",      dir: "desc", label: "Profit (high→low)" },
    { key: "sold_price",  dir: "desc", label: "Sold price (high→low)" },
    { key: "title",       dir: "asc",  label: "Title A→Z" },
  ]), []);

  return (
    <Box>
      {/* Page header — h1 serif title + count subtitle + right-aligned actions */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: "1.85rem", md: "2.5rem" } }}>
            Sales
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            {total.toLocaleString()} order{total === 1 ? "" : "s"}
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: "wrap", gap: 1, "& > *": { flex: { xs: 1, sm: "unset" }, minWidth: { xs: 0, sm: "auto" } } }}
        >
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => setModalOpen(true)}
            disabled={refreshingFromEbay}
          >
            Add sale
          </Button>
          <Button
            startIcon={refreshingFromEbay ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="contained"
            onClick={refreshFromEbay}
            disabled={refreshingFromEbay}
          >
            {refreshingFromEbay ? "Syncing…" : "Sync eBay"}
          </Button>
        </Stack>
      </Stack>

      {/* Filters card */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search title, order, lot ids…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            {PLATFORM_OPTIONS.map((p) => (
              <MenuItem key={p || "any"} value={p}>{p ? (MARKETPLACE_META[p]?.label || p) : "Any platform"}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            value={`${sortKey}|${sortDir}`}
            onChange={(e) => {
              const [k, d] = e.target.value.split("|");
              setSortKey(k);
              setSortDir(d);
            }}
            sx={{ minWidth: 200 }}
          >
            {sortOptions.map((o) => (
              <MenuItem key={o.key + o.dir} value={`${o.key}|${o.dir}`}>{o.label}</MenuItem>
            ))}
          </TextField>
          {/* Needs-fulfillment toggle — when ON, /api/sales filters server-side
              to rows with no tracking number and no return on file. Persists to
              localStorage so the seller's "what do I have to ship?" view sticks
              across reloads. */}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={needsFulfillmentOnly}
                onChange={(e) => setNeedsFulfillmentOnly(e.target.checked)}
              />
            }
            label="Needs fulfillment"
            sx={{ ml: 0.5 }}
          />
        </Stack>
      </Paper>

      {/* When the needs-fulfillment filter is on, show the count above the
          table so the seller sees "X pending" at a glance. ``total`` already
          reflects the filtered set since the backend applies the filter
          before paginate(). */}
      {needsFulfillmentOnly && (
        <Typography variant="caption" sx={{ display: "block", mb: 1, color: "warning.main", fontWeight: 600 }}>
          {total.toLocaleString()} pending fulfillment
        </Typography>
      )}

      {/* Table */}
      <Paper variant="outlined" sx={{ overflow: "hidden", position: "relative" }}>
        <TableContainer>
          <Table size="small" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => {
                  const active = sortKey === col.key;
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align || "left"}
                      style={col.width ? { width: col.width } : undefined}
                      sx={{
                        cursor: col.sortable ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={() => onHeaderClick(col)}
                    >
                      <Stack
                        direction={col.align === "right" ? "row-reverse" : "row"}
                        alignItems="center"
                        gap={0.5}
                        sx={{ display: "inline-flex" }}
                      >
                        {col.header}
                        {col.sortable && active && (
                          sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                        )}
                      </Stack>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={14} />
                      <span>Loading…</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6 }}>
                    <Stack alignItems="center" gap={1} sx={{ color: "text.secondary" }}>
                      <ShoppingCartIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>No sales</Typography>
                      <Typography variant="caption">Try adjusting your search.</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => {
                const platformKey = String(row.sold_at || "").toLowerCase();
                const meta = MARKETPLACE_META[platformKey];
                const soldAt = platformKey;
                const ebayHref = (row.order_id && soldAt.includes("ebay"))
                  ? `https://www.ebay.com/mesh/ord/details?orderid=${encodeURIComponent(String(row.order_id).trim())}`
                  : null;
                // Linked-inventory chips: cap at 3 so heavy multi-lot orders
                // don't blow up row height. The +N overflow chip is purely
                // visual — clicking it does nothing (the user can drill in
                // via any of the three visible chips).
                const linkedLotIds = Array.isArray(row.linked_lot_ids) ? row.linked_lot_ids : [];
                const linkedTitles = Array.isArray(row.linked_titles) ? row.linked_titles : [];
                const visibleChips = linkedLotIds.slice(0, 3);
                const overflowCount = Math.max(0, linkedLotIds.length - visibleChips.length);
                const tracking = (row.tracking_number || "").toString().trim();
                // Row-wide click destination, in priority order:
                //   1. eBay order page (when ebay + order_id present) — opens
                //      a new tab so the operator can pivot to "look at this
                //      sale on eBay" in one click. Most common ask.
                //   2. First linked inventory item's detail page — useful for
                //      FB / Amazon / manual sales where there's no eBay URL.
                // Chips, buttons, and inline icons stop propagation so they
                // don't accidentally trigger the row-wide nav.
                const primaryLot = linkedLotIds[0] || (row.items?.[0]?.lot_id);
                const handleRowClick = (e) => {
                  // Allow ctrl/cmd/middle-click to fall through to default
                  // browser behavior on links inside the row.
                  if (e.metaKey || e.ctrlKey || e.button === 1) return;
                  // Ignore clicks on form controls + interactive elements.
                  const t = e.target;
                  if (t.closest("button, a, input, textarea, select, .MuiChip-root, [role='button']")) return;
                  if (ebayHref) {
                    window.open(ebayHref, "_blank", "noopener,noreferrer");
                    return;
                  }
                  if (primaryLot) {
                    navigate(`/admint/inventory/${encodeURIComponent(primaryLot)}`);
                  }
                };
                const rowClickable = !!(ebayHref || primaryLot);
                return (
                  <TableRow
                    key={row.id ?? row.order_id ?? row.lot_ids_text}
                    hover
                    onClick={rowClickable ? handleRowClick : undefined}
                    sx={rowClickable ? { cursor: "pointer" } : undefined}
                    title={ebayHref ? "Open eBay order" : (primaryLot ? "Open inventory item" : undefined)}
                  >
                    <TableCell sx={{ width: 80 }}>
                      <ImageGalleryCell imageUrlsStr={row.image_urls} thumbnailSize={48} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 0, maxWidth: 360 }}>
                        <Typography
                          variant="body2"
                          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          {row.cleaned_title || row.title || "—"}
                        </Typography>
                        {row.buyer && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            Buyer: {row.buyer}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontFamily: "monospace",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                          }}
                        >
                          {row.lot_ids_text || (row.items?.[0]?.lot_id) || "—"}
                          {row.item_count > 1 && (
                            <Box component="span" sx={{ ml: 0.75, color: "text.disabled" }}>
                              +{row.item_count - 1} more
                            </Box>
                          )}
                        </Typography>
                        {visibleChips.length > 0 && (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}
                          >
                            {visibleChips.map((lotId, i) => {
                              const title = linkedTitles[i] || "";
                              const label = title
                                ? `${lotId} · ${title.length > 24 ? title.slice(0, 24) + "…" : title}`
                                : lotId;
                              return (
                                <Chip
                                  key={lotId}
                                  size="small"
                                  variant="outlined"
                                  label={label}
                                  component={RouterLink}
                                  to={`/admint/inventory/${encodeURIComponent(lotId)}`}
                                  clickable
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ height: 20, fontSize: 11, maxWidth: 220 }}
                                />
                              );
                            })}
                            {overflowCount > 0 && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`+${overflowCount}`}
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            )}
                          </Stack>
                        )}
                      </Box>
                    </TableCell>
                    {/* Qty cell — bold + warning color when buyer ordered
                        more than one (multi-qty single-line orders). The
                        number is the count of linked lots, which equals
                        the eBay line item quantity for auto-matched bulk
                        sales. */}
                    <TableCell
                      align="right"
                      sx={{
                        fontVariantNumeric: "tabular-nums",
                        fontWeight: row.item_count > 1 ? 700 : 400,
                        color: row.item_count > 1 ? "warning.dark" : "text.primary",
                      }}
                    >
                      {row.item_count || 1}
                    </TableCell>
                    <TableCell sx={{ width: 110 }}>
                      {row.sold_at ? (
                        <Chip
                          size="small"
                          label={meta?.label || row.sold_at}
                          color={meta?.color || "default"}
                          variant="outlined"
                        />
                      ) : "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {money(row.sold_price)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>
                      {money(row.spend)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>
                      {money(row.shipping_cost)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>
                      {money(row.platform_fee)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 500, color: profitColor(row.profit) }}
                    >
                      {money(row.profit)}
                    </TableCell>
                    <TableCell sx={{ width: 130 }}>
                      <SaleStatusChip
                        status={row.status}
                        refundAmount={row.refund_amount}
                        soldPrice={row.sold_price}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontFamily: "monospace", fontSize: 11, maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {tracking || "—"}
                    </TableCell>
                    <TableCell sx={{ width: 90 }}>
                      {row.needs_fulfillment ? (
                        <Chip size="small" color="warning" label="Pending" variant="filled" />
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {row.sold_date ? new Date(row.sold_date * 1000).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell sx={{ width: 92 }}>
                      <Stack direction="row" spacing={0.25} alignItems="center"
                        onClick={(e) => e.stopPropagation()}>
                        {/* Add-lots button — only useful when the sale
                            actually exists in our DB (row.id present).
                            Surfaces the bundle-matching gap: a custom
                            listing was matched to one lot but actually
                            covered several. */}
                        {row.id && (
                          <IconButton
                            size="small"
                            aria-label="Add lots to this sale"
                            title="Add lots to this sale"
                            onClick={() => {
                              setAddLotsTarget({
                                id: row.id,
                                title: row.title || "",
                                order_id: row.order_id || "",
                                existing_lot_ids: linkedLotIds,
                              });
                              setAddLotsInput("");
                            }}
                          >
                            <PlaylistAddIcon fontSize="inherit" />
                          </IconButton>
                        )}
                        {ebayHref && (
                          <MuiLink
                            href={ebayHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="View on eBay"
                            sx={{ display: "inline-flex", alignItems: "center" }}
                          >
                            <IconButton size="small" component="span">
                              <LaunchIcon fontSize="inherit" />
                            </IconButton>
                          </MuiLink>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && rows.length > 0 && (
          <Box sx={{
            position: "absolute", top: 8, right: 12,
            display: "inline-flex", alignItems: "center", gap: 0.75,
            bgcolor: "background.paper", border: 1, borderColor: "divider",
            borderRadius: 1, px: 1, py: 0.25, fontSize: "0.7rem",
            color: "text.secondary",
          }}>
            <CircularProgress size={12} /> Refreshing
          </Box>
        )}
        <Box sx={{ borderTop: 1, borderColor: "divider", bgcolor: "grey.50" }}>
          <TablePagination
            component="div"
            count={total}
            page={Math.max(0, page - 1)}
            onPageChange={(_, p) => setPage(p + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </Box>
      </Paper>

      {/* Add Sale dialog — replaces the previous custom Modal. Logic
          unchanged: same lotIds growable list, same formData fields, same
          submitSale handler. Only the rendering primitives differ. */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add sale
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Record a sold lot manually.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={submitSale} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 0.75 }}>
                Items
              </Typography>
              <Stack spacing={1}>
                {lotIds.map((id, i) => (
                  <LotPicker
                    key={i}
                    value={id}
                    onChange={(lotId) => handleLotIdChange(i, lotId)}
                    onClear={() => removeLotAt(i)}
                    autoFocus={i === lotIds.length - 1 && !id}
                    canRemove={lotIds.length > 1}
                  />
                ))}
              </Stack>
              <Typography variant="caption" sx={{ color: "text.disabled", display: "block", mt: 0.5 }}>
                Type a title or paste a lot id. A new row appears once you pick one.
              </Typography>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              <TextField
                label="Sold price"
                size="small"
                inputMode="decimal"
                value={formData.sold_price}
                onChange={(e) => setFormData((p) => ({ ...p, sold_price: e.target.value }))}
              />
              <TextField
                select
                label="Platform"
                size="small"
                value={formData.sold_at}
                onChange={(e) => setFormData((p) => ({ ...p, sold_at: e.target.value }))}
              >
                {["ebay", "amazon", "facebook", "depop", "other"].map((opt) => (
                  <MenuItem key={opt} value={opt}>{MARKETPLACE_META[opt]?.label || opt}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Order ID"
                size="small"
                value={formData.order_id}
                onChange={(e) => setFormData((p) => ({ ...p, order_id: e.target.value }))}
              />
              <TextField
                label="Tracking #"
                size="small"
                value={formData.tracking_number}
                onChange={(e) => setFormData((p) => ({ ...p, tracking_number: e.target.value }))}
              />
              <TextField
                label="Platform fee"
                size="small"
                inputMode="decimal"
                value={formData.platform_fee}
                onChange={(e) => setFormData((p) => ({ ...p, platform_fee: e.target.value }))}
              />
              <TextField
                label="Shipping cost"
                size="small"
                inputMode="decimal"
                value={formData.shipping_cost}
                onChange={(e) => setFormData((p) => ({ ...p, shipping_cost: e.target.value }))}
              />
            </Box>
            {/* Hidden submit so Enter triggers submitSale */}
            <button type="submit" hidden />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitSale}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Save sale
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add-lots-to-existing-sale dialog. Lightweight on purpose —
          the title + existing-lot list is just context; the real input
          is a single textarea where the operator pastes/types lot ids
          separated by anything reasonable (spaces, commas, newlines).
          Backend rejects per-lot duplicates with 409, so paste-with-
          overlap is safe (the dupe stops the whole batch and surfaces
          a clear error). */}
      <Dialog
        open={!!addLotsTarget}
        onClose={() => !addLotsBusy && setAddLotsTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add lots to sale #{addLotsTarget?.id}
          {addLotsTarget?.order_id && (
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
              Order {addLotsTarget.order_id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {addLotsTarget?.title && (
            <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
              {addLotsTarget.title}
            </Typography>
          )}
          {addLotsTarget?.existing_lot_ids?.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
                Already linked ({addLotsTarget.existing_lot_ids.length}):
              </Typography>
              <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap>
                {addLotsTarget.existing_lot_ids.map((lid) => {
                  // Only allow unlink when > 1 lot is present — deleting the
                  // last lot on a sale would leave a dangling sale row; backend
                  // rejects that with 409 anyway. Also disable while any dialog
                  // action is in flight.
                  const canUnlink = (addLotsTarget.existing_lot_ids?.length || 0) > 1;
                  return (
                    <Chip
                      key={lid}
                      size="small"
                      label={lid}
                      variant="outlined"
                      onDelete={canUnlink && !addLotsBusy ? async () => {
                        if (!window.confirm(`Unlink ${lid} from sale #${addLotsTarget.id}?\n\nThis frees the lot so it can be re-sold or reassigned.`)) return;
                        setAddLotsBusy(true);
                        try {
                          const res = await apiDelete(`/api/sales/${addLotsTarget.id}/lots/${encodeURIComponent(lid)}`);
                          if (res?.success) {
                            toastSuccess("Lot unlinked", `${lid} → sale #${addLotsTarget.id}${res.restored_location ? ` (restored to ${res.restored_location})` : " (no prior location on record)"}`);
                            setAddLotsTarget((cur) => cur ? {
                              ...cur,
                              existing_lot_ids: (cur.existing_lot_ids || []).filter((x) => x !== lid),
                            } : cur);
                            cacheInvalidate("/api/sales");
                            load();
                          } else {
                            toastError("Couldn't unlink", res?.error || "Unknown error");
                          }
                        } catch (err) {
                          toastError("Couldn't unlink", err instanceof ApiError ? err.message : String(err));
                        } finally {
                          setAddLotsBusy(false);
                        }
                      } : undefined}
                    />
                  );
                })}
              </Stack>
            </Box>
          )}
          <TextField
            label="Lot IDs to add"
            placeholder="ORF20754, ORF22860 NN12074..."
            value={addLotsInput}
            onChange={(e) => setAddLotsInput(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            helperText="Separate by spaces, commas, or newlines. Each lot can only belong to one sale — duplicates will be rejected."
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddLotsTarget(null)} disabled={addLotsBusy}>Cancel</Button>
          <Button
            onClick={submitAddLots}
            variant="contained"
            disabled={addLotsBusy || !addLotsInput.trim()}
            startIcon={addLotsBusy ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {addLotsBusy ? "Linking…" : "Add lots"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
